/**
 * Shared primitives and helpers used across the AI Visibility Command Hub
 * and its sub-components (HubKpis, ShareKitModal, DeliverabilityPanel).
 */

export const STATUS_PILL = {
  pending:     "bg-neutral-100 border-neutral-300 text-neutral-700",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  submitted:   "bg-emerald-50 border-emerald-300 text-emerald-800",
};

export function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function Section({ title, eyebrow, action, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="overline mb-1">{eyebrow}</div>
          <h2 className="text-lg font-bold text-neutral-900">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function Kpi({ label, value, sub, icon: Icon, testId, accent }) {
  const border = accent === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-neutral-200 bg-white";
  return (
    <div className={`border ${border} p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-neutral-500">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{value ?? "—"}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>
    </div>
  );
}

export function MiniStat({ label, value, accent }) {
  const colors = {
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className={`border p-2 ${colors[accent] || "bg-white border-neutral-200"}`}>
      <div className="text-xs font-mono uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

export function PriceCard({ title, price, desc, accent }) {
  return (
    <div className={`border p-5 ${accent ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200"}`}>
      <div className={`text-xs font-mono uppercase tracking-widest ${accent ? "text-amber-300" : "text-coral"}`}>
        Add-on
      </div>
      <div className="font-bold text-lg mt-1">{title}</div>
      <div className={`text-3xl font-bold mt-2 ${accent ? "" : "text-neutral-900"}`}>
        ${price}<span className={`text-xs font-mono ${accent ? "text-neutral-400" : "text-neutral-500"}`}>/mo</span>
      </div>
      <p className={`text-sm mt-2 ${accent ? "text-neutral-300" : "text-neutral-600"}`}>{desc}</p>
    </div>
  );
}

export function Health({ ok, warn, warnLabel, label, icon: Icon }) {
  const state = warn ? "warn" : ok ? "ok" : "off";
  const styles = {
    ok:   "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    off:  "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`border p-3 flex items-center gap-2 ${styles[state]}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <div className="flex-1">
        <div className="text-xs font-mono uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-sm font-semibold mt-0.5">
          {state === "ok" && "Online"}
          {state === "warn" && (warnLabel || "Attention")}
          {state === "off" && "Not configured"}
        </div>
      </div>
    </div>
  );
}
